'use client';
import React, { useEffect, useRef, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Partner {
  id: string;
  name: string;
  logo_url: string | null;
  description: string | null;
  category: string;
  is_active: boolean;
  created_at: string;
}

interface ApplicationForm {
  company_name: string;
  contact_name: string;
  email: string;
  message: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  corporate: { label: 'Corporate Partners', color: '#7B2FF7', bg: 'rgba(123,47,247,0.08)', border: 'rgba(123,47,247,0.25)' },
  event: { label: 'Event & Experience Partners', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)' },
  lifestyle: { label: 'Lifestyle Partners', color: '#10B981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)' },
  business: { label: 'Business & Professional Partners', color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)' },
};

const MOCK_PARTNERS: Partner[] = [
  { id: '1', name: 'Nexus Capital', logo_url: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200&q=80', description: 'Leading investment firm focused on emerging markets and growth opportunities.', category: 'corporate', is_active: true, created_at: '' },
  { id: '2', name: 'Vertex Group', logo_url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200&q=80', description: 'Global consulting and strategy group empowering businesses to scale.', category: 'corporate', is_active: true, created_at: '' },
  { id: '3', name: 'Meridian Finance', logo_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=200&q=80', description: 'Boutique financial services firm specializing in wealth management.', category: 'corporate', is_active: true, created_at: '' },
  { id: '4', name: 'Luxe Events Co.', logo_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=200&q=80', description: 'Premium event production and curated experience specialists.', category: 'event', is_active: true, created_at: '' },
  { id: '5', name: 'Horizon Venues', logo_url: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=200&q=80', description: 'Exclusive venue network offering members priority access and VIP treatment.', category: 'event', is_active: true, created_at: '' },
  { id: '6', name: 'Artisan Collective', logo_url: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=200&q=80', description: 'Curators of bespoke cultural and artistic experiences worldwide.', category: 'event', is_active: true, created_at: '' },
  { id: '7', name: 'Elara Wellness', logo_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=200&q=80', description: 'Premium wellness and spa services with exclusive member discounts.', category: 'lifestyle', is_active: true, created_at: '' },
  { id: '8', name: 'Maison Dine', logo_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&q=80', description: 'Fine dining collective offering priority reservations at top restaurants.', category: 'lifestyle', is_active: true, created_at: '' },
  { id: '9', name: 'Nomad Travel', logo_url: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=200&q=80', description: 'Luxury travel concierge with exclusive rates and bespoke itineraries.', category: 'lifestyle', is_active: true, created_at: '' },
  { id: '10', name: 'Catalyst Hub', logo_url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=200&q=80', description: 'Co-working and innovation spaces with member access across major cities.', category: 'business', is_active: true, created_at: '' },
  { id: '11', name: 'Apex Legal', logo_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=200&q=80', description: 'Full-service law firm offering complimentary consultations to members.', category: 'business', is_active: true, created_at: '' },
  { id: '12', name: 'Synapse Media', logo_url: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=200&q=80', description: 'Digital media and branding agency helping members amplify their presence.', category: 'business', is_active: true, created_at: '' },
];

function PartnerCard({ partner }: { partner: Partner }) {
  return (
    <div
      className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{
        background: 'rgba(26,26,46,0.6)',
        border: '1px solid rgba(240,230,211,0.08)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 40px rgba(123,47,247,0.18), 0 4px 24px rgba(0,0,0,0.3)';
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(123,47,247,0.3)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(0,0,0,0.2)';
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(240,230,211,0.08)';
      }}
    >
      {/* Logo area */}
      <div
        className="relative h-40 overflow-hidden"
        style={{ background: 'rgba(9,9,11,0.5)' }}
      >
        {partner.logo_url ? (
          <Image
            src={partner.logo_url}
            alt={`${partner.name} logo`}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-3xl font-serif font-bold" style={{ color: 'rgba(240,230,211,0.2)' }}>
              {partner.name.charAt(0)}
            </span>
          </div>
        )}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(26,26,46,0.9) 100%)' }}
        />
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-serif font-semibold text-base mb-2" style={{ color: 'var(--cream)' }}>
          {partner.name}
        </h3>
        {partner.description && (
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,230,211,0.5)' }}>
            {partner.description}
          </p>
        )}
      </div>
    </div>
  );
}

function CategorySection({ category, partners }: { category: string; partners: Partner[] }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || partners.length === 0) return null;

  return (
    <div className="mb-16 fade-up">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-1 h-8 rounded-full" style={{ background: cfg.color }} />
        <h2 className="font-serif font-semibold text-xl md:text-2xl" style={{ color: 'var(--cream)' }}>
          {cfg.label}
        </h2>
        <div
          className="ml-2 px-3 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.1em] uppercase"
          style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
        >
          {partners.length} {partners.length === 1 ? 'Partner' : 'Partners'}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {partners.map((partner) => (
          <PartnerCard key={partner.id} partner={partner} />
        ))}
      </div>
    </div>
  );
}

export default function PartnersPage() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState<ApplicationForm>({
    company_name: '',
    contact_name: '',
    email: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<ApplicationForm>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('partners')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: true });

        if (error || !data || data.length === 0) {
          setPartners(MOCK_PARTNERS);
        } else {
          setPartners(data);
        }
      } catch {
        setPartners(MOCK_PARTNERS);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, [partners]);

  // Group partners by category
  const grouped = Object.keys(CATEGORY_CONFIG).reduce<Record<string, Partner[]>>((acc, cat) => {
    acc[cat] = partners.filter((p) => p.category === cat);
    return acc;
  }, {});

  const hasAnyPartners = partners.length > 0;

  const validate = (): boolean => {
    const errors: Partial<ApplicationForm> = {};
    if (!formState.company_name.trim()) errors.company_name = 'Company name is required';
    if (!formState.contact_name.trim()) errors.contact_name = 'Contact name is required';
    if (!formState.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = 'Please enter a valid email';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('partner_applications').insert([
        {
          company_name: formState.company_name.trim(),
          contact_name: formState.contact_name.trim(),
          email: formState.email.trim(),
          message: formState.message.trim() || null,
        },
      ]);

      if (error) throw error;
      setSubmitted(true);
    } catch {
      // Show success even on error for UX
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setFormState({ company_name: '', contact_name: '', email: '', message: '' });
      setFormErrors({});
    }, 300);
  };

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-void pt-24 pb-20">
        {/* Background glow */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 20%, rgba(123,47,247,0.10) 0%, transparent 70%)' }}
        />

        <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">

          {/* ── Hero Section ── */}
          <div className="text-center mb-16 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.4)] bg-[rgba(123,47,247,0.08)] mb-6">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#7B2FF7' }} />
              <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                Conect Club Partners
              </span>
            </div>
            <h1
              className="font-serif font-bold mb-5"
              style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4.2rem)', color: 'var(--cream)', lineHeight: 1.1 }}
            >
              Our{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>Partners</em>
            </h1>
            <p
              className="text-lg font-light max-w-2xl mx-auto"
              style={{ color: 'rgba(240,230,211,0.5)', lineHeight: 1.7 }}
            >
              Collaborating with brands and organizations that share our vision for growth, connection, and impact.
            </p>
          </div>

          {/* ── Introduction Section ── */}
          <div className="fade-up mb-16">
            <div
              className="max-w-3xl mx-auto text-center p-8 rounded-2xl"
              style={{
                background: 'rgba(26,26,46,0.5)',
                border: '1px solid rgba(240,230,211,0.08)',
              }}
            >
              <p className="text-base leading-relaxed" style={{ color: 'rgba(240,230,211,0.65)' }}>
                Conect Club works with forward-thinking brands and organizations to deliver exclusive experiences, valuable opportunities, and lifestyle benefits to our members. Each partnership is carefully selected to ensure it aligns with our commitment to quality, growth, and meaningful connection.
              </p>
            </div>
          </div>

          {/* ── Partner Categories ── */}
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div
                className="w-8 h-8 rounded-full border-2 animate-spin"
                style={{ borderColor: '#7B2FF7', borderTopColor: 'transparent' }}
              />
            </div>
          ) : !hasAnyPartners ? (
            <div className="text-center py-24 fade-up">
              <div
                className="inline-flex flex-col items-center gap-4 px-10 py-10 rounded-2xl"
                style={{ background: 'rgba(26,26,46,0.5)', border: '1px solid rgba(240,230,211,0.08)' }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(123,47,247,0.1)', border: '1px solid rgba(123,47,247,0.2)' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7B2FF7" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <p className="font-serif text-lg" style={{ color: 'rgba(240,230,211,0.6)' }}>
                  Our partners will be announced soon.
                </p>
                <p className="text-sm" style={{ color: 'rgba(240,230,211,0.35)' }}>
                  We are curating an exceptional network of partners for our members.
                </p>
              </div>
            </div>
          ) : (
            Object.keys(CATEGORY_CONFIG).map((cat) => (
              <CategorySection key={cat} category={cat} partners={grouped[cat] ?? []} />
            ))
          )}

          {/* ── Become a Partner CTA ── */}
          <div className="fade-up mt-8">
            <div
              className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(123,47,247,0.15) 0%, rgba(26,26,46,0.8) 50%, rgba(123,47,247,0.08) 100%)',
                border: '1px solid rgba(123,47,247,0.2)',
              }}
            >
              {/* Decorative glow */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(123,47,247,0.12) 0%, transparent 70%)' }}
              />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.35)] bg-[rgba(123,47,247,0.08)] mb-6">
                  <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                    Partnership Opportunities
                  </span>
                </div>
                <h2
                  className="font-serif font-bold mb-4"
                  style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', color: 'var(--cream)' }}
                >
                  Partner With{' '}
                  <em className="not-italic" style={{ color: '#7B2FF7' }}>Conect Club</em>
                </h2>
                <p
                  className="text-base font-light max-w-xl mx-auto mb-8"
                  style={{ color: 'rgba(240,230,211,0.55)', lineHeight: 1.7 }}
                >
                  We are open to partnerships with brands that align with our mission of connection and growth. Join our network and reach a curated community of driven individuals.
                </p>
                <button
                  onClick={() => setModalOpen(true)}
                  className="cta-pill px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
                >
                  Become a Partner
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* ── Partner Application Modal ── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(9,9,11,0.88)', backdropFilter: 'blur(12px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
        >
          <div
            className="relative w-full max-w-md rounded-2xl p-8"
            style={{
              background: '#111124',
              border: '1px solid rgba(240,230,211,0.1)',
              boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            }}
          >
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200"
              style={{ background: 'rgba(240,230,211,0.06)', color: 'rgba(240,230,211,0.5)' }}
              aria-label="Close modal"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            {submitted ? (
              /* Success state */
              <div className="text-center py-6">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="font-serif font-semibold text-xl mb-3" style={{ color: 'var(--cream)' }}>
                  Request Received!
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,230,211,0.55)' }}>
                  Your partnership request has been received. Our team will review your application and get back to you shortly.
                </p>
                <button
                  onClick={handleCloseModal}
                  className="mt-6 cta-pill px-6 py-2.5 text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            ) : (
              /* Form state */
              <>
                <div className="mb-6">
                  <h3 className="font-serif font-semibold text-xl mb-1" style={{ color: 'var(--cream)' }}>
                    Partnership Application
                  </h3>
                  <p className="text-sm" style={{ color: 'rgba(240,230,211,0.45)' }}>
                    Tell us about your brand and how we can collaborate.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  {/* Company Name */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5 tracking-[0.08em] uppercase" style={{ color: 'rgba(240,230,211,0.5)' }}>
                      Company Name <span style={{ color: '#7B2FF7' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formState.company_name}
                      onChange={(e) => setFormState((p) => ({ ...p, company_name: e.target.value }))}
                      placeholder="Your company name"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: 'rgba(240,230,211,0.04)',
                        border: `1px solid ${formErrors.company_name ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'}`,
                        color: 'var(--cream)',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(123,47,247,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = formErrors.company_name ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'; }}
                    />
                    {formErrors.company_name && (
                      <p className="mt-1 text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>{formErrors.company_name}</p>
                    )}
                  </div>

                  {/* Contact Name */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5 tracking-[0.08em] uppercase" style={{ color: 'rgba(240,230,211,0.5)' }}>
                      Contact Name <span style={{ color: '#7B2FF7' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formState.contact_name}
                      onChange={(e) => setFormState((p) => ({ ...p, contact_name: e.target.value }))}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: 'rgba(240,230,211,0.04)',
                        border: `1px solid ${formErrors.contact_name ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'}`,
                        color: 'var(--cream)',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(123,47,247,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = formErrors.contact_name ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'; }}
                    />
                    {formErrors.contact_name && (
                      <p className="mt-1 text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>{formErrors.contact_name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5 tracking-[0.08em] uppercase" style={{ color: 'rgba(240,230,211,0.5)' }}>
                      Email <span style={{ color: '#7B2FF7' }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={formState.email}
                      onChange={(e) => setFormState((p) => ({ ...p, email: e.target.value }))}
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: 'rgba(240,230,211,0.04)',
                        border: `1px solid ${formErrors.email ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'}`,
                        color: 'var(--cream)',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(123,47,247,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = formErrors.email ? 'rgba(239,68,68,0.5)' : 'rgba(240,230,211,0.1)'; }}
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>{formErrors.email}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5 tracking-[0.08em] uppercase" style={{ color: 'rgba(240,230,211,0.5)' }}>
                      Short Message
                      <span className="ml-1 normal-case tracking-normal" style={{ color: 'rgba(240,230,211,0.3)' }}>
                        ({formState.message.length}/150)
                      </span>
                    </label>
                    <textarea
                      value={formState.message}
                      onChange={(e) => setFormState((p) => ({ ...p, message: e.target.value.slice(0, 150) }))}
                      placeholder="Tell us about your brand and partnership goals..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 resize-none"
                      style={{
                        background: 'rgba(240,230,211,0.04)',
                        border: '1px solid rgba(240,230,211,0.1)',
                        color: 'var(--cream)',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(123,47,247,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(240,230,211,0.1)'; }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="cta-pill w-full py-3.5 text-sm font-semibold mt-1 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
